import {Performance} from "@/assets/js/tactics/pitch/responsibilities";
import {
  CENTRAL_MIDFIELD,
  DEFENSIVE_MIDFIELD,
  WIDE_MIDFIELD
} from "@/assets/js/position-data";
import Partner from "@/assets/js/partner";
import PartnerTypes from "@/assets/js/partner-types";
import PairWith from "@/assets/js/pair-with";
import Strata from "@/assets/js/strata";
import Saga from "@/assets/js/saga";
import PositionGroups from "@/assets/js/position-groups";
import Duties from '@/assets/js/duties.js';


export default class CentralMidfield {
  constructor(position) {
    this.position = position;
  }

  get responsibilityPerformance() {
    return {
      defensivePressure: this.defensivePressure(),
      creatingChances: this.creatingChances(),
      playingSafe: this.playingSafe(),
      attackingCover: this.attackingCover(),
      defensiveCover: this.defensiveCover(),
      stretchingPlay: this.stretchingPlay(),
    };
  }

  defensivePressure() {
    const midfielderDoesntHoldOff = 
        [CENTRAL_MIDFIELD, DEFENSIVE_MIDFIELD].includes(this.position.positionGroup) && 
        this.position.playerInstructions.pressingIntensity >= 0;
    const pressingWideMidfielder = 
        this.position.stratum === Strata.M && 
        this.position.sagum === Saga.flank && 
        this.pressingIntensity > 0;
    const performs = midfielderDoesntHoldOff || pressingWideMidfielder;

    return new Performance(performs, false, false);
  }

  creatingChances() {
    const positionOkay =
      this.position.stratum.height <= Strata.M.height &&
      (this.position.sagum === Saga.central || this.position.playerInstructions.lateralDribbling > 0);

    const playmaker = this.position.playerInstructions.creativeFreedom > 0;

    const usesTacticalDiscipline = this.position.playerInstructions.useMoreTacticalDiscipline > 0;
    const staysDeep = 
      (this.position.positionGroup === PositionGroups.fullBack && this.position.playerInstructions.movement < 0) ||
      (this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 0);

    const performs = positionOkay && (playmaker || (!usesTacticalDiscipline && !staysDeep));

    return new Performance(performs, false, false);
  }

  playingSafe() {
    /** TODO
     * Vacates:
     * DM + Drop deep more
     * MC + get further forward
     */
    const positionOkay = [Strata.DM, Strata.M].includes(this.position.stratum) ||
        (this.position.positionGroup === WIDE_MIDFIELD && this.position.playerInstructions.attackingWidth < 0);

    const performs = positionOkay &&
        this.position.playerInstructions.dribbling < 1 &&
        this.position.playerInstructions.shooting < 1 &&
        this.position.playerInstructions.passingRisks < 1 &&
        this.position.playerInstructions.creativeFreedom < 1;

    const contributes = false;

    const atypical = 
        this.position.playerInstructions.makeMoreRoamingMovementLaterally > 0 ||
        this.position.playerInstructions.moveIntoChannels > 0 ||
        this.position.playerInstructions.roamFromPosition > 0;

    return new Performance(performs, contributes, atypical);
  }

  attackingCover() {
    // TODO: atypical contributions are made by SEG-su and AP-at because
    // they have an 'attacking focus'

    const invertingWidePlayer = [Strata.DM, Strata.M].includes(this.position.stratum) && this.position.sagum === Saga.flank && this.position.playerInstructions.attackingWidth < 0;
    const pivot = [Strata.DM, Strata.M].includes(this.position.stratum) && this.position.sagum === Saga.central && this.position.playerInstructions.attackingWidth < 1;
    const libero = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1;

    const positionOkay = invertingWidePlayer || pivot || libero;
    
    const performs = positionOkay && 
        this.position.playerInstructions.movement < 0 && 
        this.position.playerInstructions.makeMoreSurgingForwardRuns < 1;
    const contributes = positionOkay && 
        this.position.playerInstructions.movement === 0 && 
        this.position.playerInstructions.makeMoreSurgingForwardRuns < 1;

    return new Performance(performs, contributes, false);
  }

  defensiveCover() {
    /**
     * TODO: atypical contributions are made by SEG-su and AP-at because
     * they have an 'attacking focus' 
     */
    const performs = [Strata.DM, Strata.M].includes(this.position.stratum) &&
        this.position.playerInstructions.pressingIntensity < 1;

    return new Performance(performs, false, false);
  }

  stretchingPlay() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const makesMoreDisciplinedMovement = this.position.playerInstructions.makesMoreDisciplinedMovement > 0;
    const midfieldRole = [Strata.DM, Strata.M].includes(this.position.stratum);
    const validDefenceRole =
        this.position.stratum === Strata.D && 
        this.position.playerInstructions.moveIntoCentralMidfieldInPossession ===1 &&
        this.position.selectedRole.duty === Duties.support;
    const centralOrNarrowRole = this.position.sagum === Saga.central || this.position.playerInstructions.attackingWidth < 0;
    const positionOkay = (midfieldRole || validDefenceRole) && centralOrNarrowRole;
    const performs = positionOkay && !holdsPosition && !makesMoreDisciplinedMovement;

    return new Performance(performs, false, false);
  }

  get partnerability() {
    return {
      lowRiskDirect: this.lowRiskDirect(),
      mediumRiskOverlapping: this.mediumRiskOverlapping(),
      mediumRiskDirect: this.mediumRiskDirect(),
      interchanging: this.interchanging(),
      highRiskDirect: this.highRiskDirect(),
      highRiskOverlapping: this.highRiskOverlapping(),
    };
  }

  lowRiskDirect() {
    const holdsPosition = this.position.playerInstructions.movement < 0;

    if (!this._possiblePositionalArea()) return false;
    if (this.position.playerInstructions.movement > 0) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    const pair = holdsPosition ?
      new PairWith(PartnerTypes.attacking, effectiveAttackingStratum, Strata.M) :
      new PairWith(PartnerTypes.ancillary, Strata.D, effectiveAttackingStratum);

    return new Partner(
      holdsPosition ? PartnerTypes.ancillary : PartnerTypes.attacking, 
      effectiveAttackingStratum, 
      pair
    );
  }

  mediumRiskOverlapping() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward > 0;

    if (!this._possiblePositionalArea()) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    if (getsFurtherForward) {
      if (this.position.stratum === Strata.ST) return false;
      return new Partner(
        PartnerTypes.attacking,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === effectiveAttackingStratum.height + 1),
          Strata.M
        )
      );
    }

    if (holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.D,
          Object.values(Strata).find(stratum => stratum.height === effectiveAttackingStratum.height - 1)
        )
      );
    }

    return false;
  }

  mediumRiskDirect() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward > 0;

    if (!this._possiblePositionalArea()) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.D,
          effectiveAttackingStratum
        )
      );
    }
    
    if (holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.attacking,
          effectiveAttackingStratum,
          Strata.M
        )
      );
    }

    return false;
  }

  interchanging() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward > 0;
    if (!this._possiblePositionalArea()) return false;

    if (this.position.selectedRole.duty !== Duties.support) return false;
    if (holdsPosition) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    return new Partner(
      getsFurtherForward ? PartnerTypes.overloading : PartnerTypes.balanced,
      effectiveAttackingStratum,
      new PairWith(
        getsFurtherForward ? PartnerTypes.overloading : PartnerTypes.balanced,
        Strata.D,
        Strata.M
      )
    );
  }

  highRiskDirect() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward > 0;

    if (!this._possiblePositionalArea()) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.D,
          effectiveAttackingStratum
        )
      );
    }
    
    if (!holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.attacking,
          effectiveAttackingStratum,
          Strata.M
        )
      );
    }

    return false;
  }

  highRiskOverlapping() {
    const holdsPosition = this.position.playerInstructions.movement < 0;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward > 0;

    if (!this._possiblePositionalArea()) return false;
    if (holdsPosition) return false;

    const effectiveAttackingStratum = this.position.positionGroup === PositionGroups.centralDefence && this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 1 ?
        Strata.DM :
        this.position.stratum;

    if (getsFurtherForward) {
      if (this.position.stratum === Strata.ST) return false;
      return new Partner(
        PartnerTypes.attacking,
        effectiveAttackingStratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === effectiveAttackingStratum.height + 1),
          Strata.M
        )
      );
    }

    return new Partner(
      PartnerTypes.ancillary,
      effectiveAttackingStratum,
      new PairWith(
        PartnerTypes.attacking,
        Strata.D,
        Object.values(Strata).find(stratum => stratum.height === effectiveAttackingStratum.height - 1)
      )
    );
  }

  _possiblePositionalArea() {
    if (!([ Strata.D, Strata.DM, Strata.M ].includes(this.position.stratum))) {
      return false;
    }
    // only allow liberos in CD
    if (this.position.positionGroup === PositionGroups.centralDefence &&
        this.position.playerInstructions.moveIntoCentralMidfieldInPossession === 0) {
      return false;
    }

    // only allow narrow wide players, but not inverted full-backs
    if (this.position.sagum === Saga.flank &&
      (this.position.playerInstructions.attackingWidth >= 0 || this.position.playerInstructions.moveIntoCentralDefenceInPossession === 1)) {
      return false;
    }
    return true;
  }
}
